import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CourseFile,
	CourseFileSchema,
	Courses,
	CoursesResponse,
	CoursesResponseSchema,
	CoursesSchema,
	CourseTag,
	CourseTagSchema,
	GenerateCourse,
	GeneratedCourse,
	GeneratedCourseSchema,
	TtsVoice,
	TtsVoiceSchema,
	CourseVersion,
	CourseVersionSchema,
} from "../../pages/Courses/schema/courses";

export const coursesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCourses: builder.query<CoursesResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "course",
				params: {
					...(params && { ...params }),
				},
				responseSchema: CoursesResponseSchema,
			}),
			providesTags: ["Courses"],
		}),
		getTags: builder.query<CourseTag[], void>({
			query: () => ({
				url: "course/tags",
				responseSchema: CourseTagSchema,
			}),
		}),
		getCourse: builder.query<Courses, number>({
			query: (id: number) => ({
				url: `course/${id}`,
				params: {
					id,
				},
				responseSchema: CoursesSchema,
			}),
			providesTags: ["Courses"],
		}),
		getCourseFiles: builder.query<CourseFile[], number>({
			query: (id: number) => ({
				url: `course/${id}/files`,
				params: {
					id,
				},
				responseSchema: CourseFileSchema.array(),
			}),
			providesTags: ["Courses"],
		}),
		getRelatedCourses: builder.query<Courses[], number>({
			query: (id: number) => ({
				url: `course/${id}/related`,
				responseSchema: CoursesSchema.array(),
			}),
			providesTags: ["RelatedCourses"],
		}),
		createCourses: builder.mutation<Partial<Courses>, Partial<Courses>>({
			query: (body) => {
				const { json, ...rest } = body;

				return {
					url: `course`,
					method: "POST",
					body: {
						...rest,
						json: typeof json === "string" ? json : JSON.stringify(json),
					},
					responseSchema: CoursesSchema,
				};
			},
			invalidatesTags: ["Courses"],
		}),
		duplicateCourses: builder.mutation<Courses, { id: number; name?: string; companyId?: number }>({
			query: ({ id, ...body }) => ({
				url: `course/duplicate/${id}`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["Courses"],
		}),

		generateCourse: builder.mutation<GeneratedCourse, GenerateCourse>({
			query: (body) => {
				return {
					url: `course/generate`,
					method: "POST",
					body,
					responseSchema: GeneratedCourseSchema,
				};
			},
			invalidatesTags: ["Courses"],
		}),
		updateCourses: builder.mutation<
			Partial<Courses>,
			Partial<Courses> & { draftRevision?: number }
		>({
			query: ({ id, json, ...rest }) => {
				return {
					url: `course/${id}`,
					method: "PATCH",
					body: {
						...rest,
						json: typeof json === "string" ? json : JSON.stringify(json),
					},
					responseSchema: CoursesSchema,
				};
			},
			// A save writes the scenario's draft, so the version list and the "there is
			// something to publish" state both change with it — BCM360-139. Without this the
			// Publish button stays disabled after the very edit it exists to publish.
			invalidatesTags: ["Courses", "CourseVersions"],
		}),
		deleteCourses: builder.mutation<Partial<Courses>, number>({
			query: (id) => ({
				url: `course/${id}`,
				method: "DELETE",
				responseSchema: CoursesSchema,
			}),
			invalidatesTags: ["Courses"],
		}),
		uploadCourseFiles: builder.mutation<Partial<CourseFile>[], FormData>({
			query: (body) => {
				return {
					url: "course/files/upload",
					method: "POST",
					body,
				};
			},
		}),
		deleteCourseFile: builder.mutation<void, { filePath: string; courseId?: number }>({
			query: ({ filePath, courseId }) => ({
				url: `course/file/delete`,
				method: "DELETE",
				body: {
					filePath,
					courseId,
				},
			}),
		}),
		generateTts: builder.mutation<
			CourseFile,
			{
				courseId: number;
				text: string;
				voiceId: string;
				modelId?: string;
				language?: "DE" | "EN";
			}
		>({
			query: (body) => ({
				url: "course/tts/generate",
				method: "POST",
				body,
				responseSchema: CourseFileSchema,
			}),
		}),
		/**
		 * The scenario's saved versions, newest first — BCM360-139.
		 *
		 * Tagged so publishing refreshes the list: the server may add a
		 * "Stand vor Veröffentlichung" snapshot of its own while publishing, so what comes
		 * back afterwards is not simply what was sent plus a flag.
		 */
		getCourseVersions: builder.query<CourseVersion[], number>({
			query: (id) => ({
				url: `course/${id}/versions`,
				responseSchema: CourseVersionSchema.array(),
			}),
			providesTags: ["CourseVersions"],
		}),

		/** Snapshots the scenario as it stands. An empty body is valid — the label is a courtesy. */
		createCourseVersion: builder.mutation<CourseVersion, { id: number; label?: string }>({
			query: ({ id, ...body }) => ({
				url: `course/${id}/versions`,
				method: "POST",
				body,
				responseSchema: CourseVersionSchema,
			}),
			invalidatesTags: ["CourseVersions"],
		}),

		/**
		 * Renaming, today. The endpoint also takes the content fields, so editing a version
		 * in place is a matter of sending more of them.
		 */
		updateCourseVersion: builder.mutation<
			CourseVersion,
			{ id: number; versionId: number; label?: string }
		>({
			query: ({ id, versionId, ...body }) => ({
				url: `course/${id}/versions/${versionId}`,
				method: "PATCH",
				body,
				responseSchema: CourseVersionSchema,
			}),
			invalidatesTags: ["CourseVersions"],
		}),

		/**
		 * Makes this version the one everybody plays, web and VR alike. Invalidates the
		 * scenario as well as the list: publishing copies the snapshot onto the course, so
		 * its content and its `publishedVersionId` both change.
		 */
		publishCourseVersion: builder.mutation<void, { id: number; versionId: number }>({
			query: ({ id, versionId }) => ({
				url: `course/${id}/versions/${versionId}/publish`,
				method: "POST",
			}),
			invalidatesTags: ["CourseVersions", "Courses", "Course"],
		}),

		/** Removes one saved state. The draft and the published version are refused by the API. */
		deleteCourseVersion: builder.mutation<void, { id: number; versionId: number }>({
			query: ({ id, versionId }) => ({
				url: `course/${id}/versions/${versionId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["CourseVersions"],
		}),

		/**
		 * Puts the draft live — the single write the published content takes from the editor.
		 * 400s when there is no draft, and refuses a draft whose scenario JSON is incomplete,
		 * so the caller has a message worth showing rather than a silent failure.
		 */
		publishCourseDraft: builder.mutation<void, number>({
			query: (id) => ({
				url: `course/${id}/publish`,
				method: "POST",
			}),
			invalidatesTags: ["CourseVersions", "Courses", "Course"],
		}),

		/**
		 * Hides the scenario again — the replacement for the removed status toggle. Content
		 * and versions are untouched, and publishing is how it comes back.
		 */
		unpublishCourse: builder.mutation<void, number>({
			query: (id) => ({
				url: `course/${id}/unpublish`,
				method: "POST",
			}),
			invalidatesTags: ["CourseVersions", "Courses", "Course"],
		}),

		getTtsVoices: builder.query<TtsVoice[], { language?: string } | void>({
			query: (params) => ({
				url: "course/tts/voices",
				params: params ? { language: params.language } : undefined,
				responseSchema: TtsVoiceSchema.array(),
			}),
		}),
	}),
});

export const {
	useLazyGetCoursesQuery,
	useGetTagsQuery,
	useGetCourseQuery,
	useGetRelatedCoursesQuery,
	useUpdateCoursesMutation,
	useDeleteCoursesMutation,
	useCreateCoursesMutation,
	useDeleteCourseFileMutation,
	useUploadCourseFilesMutation,
	useGenerateCourseMutation,
	useDuplicateCoursesMutation,
	useGetCourseFilesQuery,
	useGenerateTtsMutation,
	useGetTtsVoicesQuery,
	useGetCourseVersionsQuery,
	useCreateCourseVersionMutation,
	useUpdateCourseVersionMutation,
	usePublishCourseVersionMutation,
	usePublishCourseDraftMutation,
	useUnpublishCourseMutation,
	useDeleteCourseVersionMutation,
} = coursesApi;
export const accountApiReducer = coursesApi.reducer;
export const accountApiMiddleware = coursesApi.middleware;
